import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface AIPricingToolsProps {
  name: string;
  category: string;
  price: string;
  onResult: (description: string) => void;
}

export const AIPricingTools = ({ name, category, price, onResult }: AIPricingToolsProps) => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const generateDescription = async () => {
    if (!name.trim()) {
      toast({
        title: "Name required",
        description: "Please enter a name first to generate a description.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("ai-content", {
        body: {
          type: "pricing-description",
          title: name,
          description: price,
          galleryType: category,
        },
      });

      if (error) throw error;

      if (data?.error) {
        toast({
          title: "AI Error",
          description: data.error,
          variant: "destructive",
        });
        return;
      }

      if (data?.result) {
        onResult(data.result.trim());
        toast({ title: "Description generated" });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to generate description",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={generateDescription}
      disabled={loading}
      className="h-7 px-2 text-xs"
    >
      {loading ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <>
          <Sparkles className="h-3 w-3 mr-1" />
          AI
        </>
      )}
    </Button>
  );
};
